import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, EMPTY, of } from 'rxjs';
import { expand, map, reduce, shareReplay, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
    ConfirmatoryTestingScheduleResponseDto,
    CreateConfirmatoryTestingScheduleRequestDto,
    UpdateConfirmatoryTestingScheduleRequestDto,
    YearlyTestRecordResponseDto,
    CreateYearlyTestRecordRequestDto,
    UpdateYearlyTestRecordRequestDto,
} from '@qc/shared';
import { ResponsePaginatedWrapper, ResponseSingleWrapper } from '@/app/shared/dto';

type Schedule = ConfirmatoryTestingScheduleResponseDto;
type YearlyTestRecord = YearlyTestRecordResponseDto;

@Injectable({
    providedIn: 'root',
})
export class ConfirmatoryTestingService extends ApiService {
    protected get basePath(): string {
        return '/confirmatory-testing';
    }

    // Cache for individual schedules
    private schedulesCache = new Map<string, Observable<Schedule>>();

    private fetchAllPages<T>(url: string): Observable<T[]> {
        const getPage = (pageNumber: number): Observable<ResponsePaginatedWrapper<T>> => {
            const params = new HttpParams().set('page', pageNumber.toString()).set('limit', '100');
            return this.http.get<ResponsePaginatedWrapper<T>>(url, { params });
        };

        return getPage(1).pipe(
            expand((response) => {
                if (response.meta.currentPage < response.meta.totalPages) {
                    return getPage(response.meta.currentPage + 1);
                }
                return EMPTY;
            }),
            map((response) => response.data),
            reduce((acc, data) => acc.concat(data), [] as T[]),
        );
    }

    getAllSchedules(): Observable<Schedule[]> {
        return this.fetchAllPages<Schedule>(this.schedulesApiUrl).pipe(
            map((schedules) => schedules.map((schedule) => this.normalizeSchedule(schedule))),
        );
    }

    searchSchedules(
        searchTerm: string = '',
        page: number = 1,
        limit: number = 50,
    ): Observable<{ items: Schedule[]; meta: any }> {
        const params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString())
            .set('search', searchTerm);

        return this.getPaginated<Schedule>('', params).pipe(
            map((response: ResponsePaginatedWrapper<Schedule>) => ({
                items: response.data.map((schedule) => this.normalizeSchedule(schedule)),
                meta: response.meta,
            })),
        );
    }

    getSchedule(id: string): Observable<Schedule> {
        const cached = this.schedulesCache.get(id);
        if (cached) {
            return cached;
        }

        const request$ = this.get<Schedule>(`${id}`).pipe(
            map((response: ResponseSingleWrapper<Schedule>) => this.normalizeSchedule(response.data)),
            shareReplay(1),
        );

        this.schedulesCache.set(id, request$);
        return request$;
    }

    getScheduleByProduct(productId: string): Observable<Schedule | null> {
        return this.http
            .get<ResponseSingleWrapper<Schedule>>(`${this.apiUrl}/products/${productId}/confirmatory-testing`, {
                withCredentials: true,
            })
            .pipe(
                map((response: ResponseSingleWrapper<Schedule>) =>
                    response?.data ? this.normalizeSchedule(response.data) : null,
                ),
            );
    }

    createSchedule(scheduleData: CreateConfirmatoryTestingScheduleRequestDto): Observable<Schedule> {
        return this.post<Schedule>('', scheduleData).pipe(
            map((response: ResponseSingleWrapper<Schedule>) => this.normalizeSchedule(response.data)),
            tap((schedule) => {
                this.clearAllSchedulesCache();
                if (schedule?.id) {
                    this.clearScheduleCache(schedule.id);
                }
            }),
        );
    }

    updateSchedule(id: string, scheduleData: UpdateConfirmatoryTestingScheduleRequestDto): Observable<Schedule> {
        return this.patch<Schedule>(`${id}`, scheduleData).pipe(
            map((response: ResponseSingleWrapper<Schedule>) => this.normalizeSchedule(response.data)),
            tap((schedule) => {
                this.clearScheduleCache(id);
                if (schedule?.id) {
                    this.schedulesCache.set(schedule.id, of(schedule));
                }
            }),
        );
    }

    deleteSchedule(id: string): Observable<void> {
        return this.deleteVoid(`${id}`).pipe(
            tap(() => {
                this.clearScheduleCache(id);
                this.clearAllSchedulesCache();
            }),
        );
    }

    // Yearly Test Records Management
    addYearlyTest(scheduleId: string, yearlyTestData: CreateYearlyTestRecordRequestDto): Observable<YearlyTestRecord> {
        return this.post<YearlyTestRecord>(`${scheduleId}/yearly-tests`, yearlyTestData).pipe(
            map((response: ResponseSingleWrapper<YearlyTestRecord>) => this.normalizeYearlyTest(response.data)),
            tap(() => this.clearScheduleCache(scheduleId)),
        );
    }

    updateYearlyTest(
        scheduleId: string,
        yearId: string,
        yearlyTestData: UpdateYearlyTestRecordRequestDto,
    ): Observable<YearlyTestRecord> {
        return this.patch<YearlyTestRecord>(`${scheduleId}/yearly-tests/${yearId}`, yearlyTestData).pipe(
            map((response: ResponseSingleWrapper<YearlyTestRecord>) => this.normalizeYearlyTest(response.data)),
            tap(() => this.clearScheduleCache(scheduleId)),
        );
    }

    deleteYearlyTest(scheduleId: string, yearId: string): Observable<void> {
        return this.deleteVoid(`${scheduleId}/yearly-tests/${yearId}`).pipe(tap(() => this.clearScheduleCache(scheduleId)));
    }

    // Check if confirmatory testing is due for a product
    checkIfDue(productId: string): Observable<{ required: boolean; reason: string; schedule?: Schedule }> {
        return this.get<{ required: boolean; reason: string; schedule?: Schedule }>(`check/${productId}`).pipe(
            map((response: ResponseSingleWrapper<{ required: boolean; reason: string; schedule?: Schedule }>) => {
                const payload = response.data;

                if (!payload) {
                    return {
                        required: false,
                        reason: 'No confirmatory testing schedule configured for this product',
                    };
                }

                return {
                    ...payload,
                    schedule: payload.schedule ? this.normalizeSchedule(payload.schedule) : undefined,
                };
            }),
        );
    }

    clearScheduleCache(id: string): void {
        this.schedulesCache.delete(id);
    }

    clearAllSchedulesCache(): void {
        this.schedulesCache.clear();
    }

    private normalizeSchedule(schedule: Schedule): Schedule {
        if (!schedule) {
            return schedule;
        }

        const yearlyTests = (schedule.yearlyTests || []).map((test) => this.normalizeYearlyTest(test));

        return {
            ...schedule,
            yearlyTests,
            createdAt: schedule.createdAt ? new Date(schedule.createdAt) : schedule.createdAt,
            updatedAt: schedule.updatedAt ? new Date(schedule.updatedAt) : schedule.updatedAt,
        } as Schedule;
    }

    private normalizeYearlyTest(test: YearlyTestRecord): YearlyTestRecord {
        if (!test) {
            return test;
        }

        return {
            ...test,
            testingCompleteDate: test.testingCompleteDate ? new Date(test.testingCompleteDate) : undefined,
            createdAt: test.createdAt ? new Date(test.createdAt) : test.createdAt,
            updatedAt: test.updatedAt ? new Date(test.updatedAt) : test.updatedAt,
        } as YearlyTestRecord;
    }

    private get schedulesApiUrl(): string {
        return `${this.apiUrl}/confirmatory-testing`;
    }
}

